import { z } from 'zod';
import { FabricFacesSchema } from './fabric-faces.schema';
import {
  BanquettePolylineSchema,
  BanquetteRailSchema,
  BanquetteAdditionalPanelSchema,
  PanelRoleEnum,
} from './banquette-base-template.schema';
import { BackCushionDesignTypeEnum } from './banquette-back-cushion-template.schema';
import { PanelFinishSchema } from './panel-finish.schema';
import { FoamDimLinkSchema } from './upholstery-design.schema';

// ── Upholstery Design ──
// Reuses BackCushionDesignTypeEnum — arms share the same design vocabulary as
// the cushions. The difference is the axis the design runs along, which lives
// in fabric-pieces.ts, not here.
export { BackCushionDesignTypeEnum as ArmDesignTypeEnum };

export const ArmDesignSchema = z.object({
  type: BackCushionDesignTypeEnum.default('plain'),
  channel_width: z.number().min(0).nullable().default(null),
  channel_count_override: z.number().int().min(0).nullable().default(null),
  button_tuftings_per_foot: z.number().int().min(0).nullable().default(null),
  button_hardware_id: z.string().uuid().nullable().default(null),
  wire_hardware_id: z.string().uuid().nullable().default(null),
});

// ── Framing ──
// Differs from SeatCushionFramingSchema: no ribs (rib_material_id, rib_mode,
// rib_spacing, rib_count), and curvature is a boolean rather than a count of
// curved sections, because an arm is a single unit.

export const ArmFramingSchema = z.object({
  side_panel_material_id: z.string().uuid().nullable().default(null),
  /** Profile used by any finish-enabled panel that does not name its own. */
  default_finish_id: z.string().uuid().nullable().default(null),
  side_panel_finish: PanelFinishSchema,
  waste_factor: z.number().min(0).max(1).default(0.10),
  curved: z.boolean().default(false),
  curved_waste_factor: z.number().min(0).max(1).default(0),
  rails: z.array(BanquetteRailSchema).default([]),
  additional_panels: z.array(BanquetteAdditionalPanelSchema).default([]),
});

// ── Foam Layer ──
// Differs from SeatCushionFoamLayerSchema: explicit width AND depth. The seat
// cushion derives length from linear footage; the arm does not have any.

export const ArmFoamLayerSchema = z.object({
  name: z.string(),
  width: z.number().min(0),
  /** When set, `width` is the base and tracks this component axis. */
  width_link: FoamDimLinkSchema,
  depth: z.number().min(0),
  /** When set, `depth` is the base and tracks this component axis. */
  depth_link: FoamDimLinkSchema,
  material_id: z.string().uuid().nullable(),
});

// ── Slot ──

export const ArmSlotSchema = z.object({
  id: z.string(),
  name: z.string(),
  material_id: z.string().uuid().nullable(),
  color: z.string(),
  calc_mode: z.enum(['linear', 'area']).default('linear'),
  panel_role: PanelRoleEnum.default('front'),
  polylines: z.array(BanquettePolylineSchema).default([]),
  finish_id_side1: z.string().uuid().nullable().optional(),
  finish_id_side2: z.string().uuid().nullable().optional(),
  // Fabric-specific (only used when material has fabric_subtype)
  fabric_wrap_value: z.number().nullable().default(null),
  fabric_staple_allowance: z.number().default(1),
});

// ── Main Config ──
//
// Differs from SeatCushionConfig:
//   - product_type literal is 'banquette_arm'
//   - No lf_quantity / manual_sections / section_count — an arm is one discrete
//     unit sized by width x depth x height, priced per unit
//   - No substrate_mode / backer_panel — arms are always full frame
//   - No springs
//   - Dacron carries its own width x depth instead of being derived from LF
export const ArmConfigSchema = z.object({
  product_type: z.literal('banquette_arm'),
  code: z.string(),
  name: z.string(),

  // Dimensions, inches. depth is the design axis.
  width: z.number().min(0).default(6),
  depth: z.number().min(0).default(24),
  height: z.number().min(0).default(26),
  /** Reference depth the dynamic panels were authored against. Null = never
   *  baselined, which makes every delta zero. Must never auto-follow the live
   *  dimension — see the spec's freezing rule. */
  base_depth: z.number().min(0).nullable().default(null),
  /** Reference height the dynamic panels were authored against. */
  base_height: z.number().min(0).nullable().default(null),
  /** Reference width the dynamic foam layers were authored against. Null =
   *  never baselined, which makes every delta zero. Must never auto-follow the
   *  live dimension — see the spec's freezing rule. */
  base_width: z.number().min(0).nullable().default(null),

  design: ArmDesignSchema,
  framing: ArmFramingSchema,

  // Foam layers (each layer is one explicit width x depth piece)
  foam_layers: z.array(ArmFoamLayerSchema).default([]),
  foam_waste_factor: z.number().min(0).max(1).default(0.10),
  /** Extra foam waste when framing.curved is on. An arm is a single unit, so
   *  this applies in full or not at all. */
  foam_curved_waste_factor: z.number().min(0).max(1).default(0),

  dacron_material_id: z.string().uuid().nullable().default(null),
  dacron_width: z.number().min(0).default(0),
  dacron_depth: z.number().min(0).default(0),
  dacron_waste_factor: z.number().min(0).max(1).default(0.10),
  /** Extra dacron waste when framing.curved is on. */
  dacron_curved_waste_factor: z.number().min(0).max(1).default(0),

  // Per-line-item fabric allowances
  fabric_design_pct: z.number().min(0).nullable().optional(),
  fabric_pattern_allowance_pct: z.number().min(0).default(0),
  /** Which box faces receive fabric. Absent means all six — see resolveFaces. */
  fabric_faces: FabricFacesSchema.optional(),

  // Slots
  viewport_id: z.string().uuid().nullable().optional(),
  image_scale_ppi: z.number().min(0).default(0),
  slots: z.array(ArmSlotSchema).default([]),
});

// ── Template CRUD ──

export const ArmTemplateSchema = z.object({
  id: z.string().uuid(),
  code: z.string(),
  name: z.string(),
  description: z.string().nullable(),
  arm_config: z.string(),
  active: z.boolean(),
  created_by: z.string().uuid().nullable(),
  created_at: z.string().datetime(),
  updated_at: z.string().datetime(),
});

export const CreateArmTemplateSchema = z.object({
  code: z.string().min(1, 'Code is required'),
  name: z.string().min(1, 'Name is required'),
  description: z.string().nullable().optional(),
  arm_config: z.string().default('{}'),
  active: z.boolean().default(true),
});

export const UpdateArmTemplateSchema = CreateArmTemplateSchema.partial();

// ── Type exports ──

export type ArmDesignType = z.infer<typeof BackCushionDesignTypeEnum>;
export type ArmDesign = z.infer<typeof ArmDesignSchema>;
export type ArmFraming = z.infer<typeof ArmFramingSchema>;
export type ArmFoamLayer = z.infer<typeof ArmFoamLayerSchema>;
export type ArmSlot = z.infer<typeof ArmSlotSchema>;
export type ArmConfig = z.infer<typeof ArmConfigSchema>;
export type ArmTemplate = z.infer<typeof ArmTemplateSchema>;
export type CreateArmTemplate = z.infer<typeof CreateArmTemplateSchema>;
export type UpdateArmTemplate = z.infer<typeof UpdateArmTemplateSchema>;
